import React, { Suspense, lazy, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { hapticTick } from '../../../_utils/haptics';
import { MESSAGES_VISIBLE } from '../../../config/featureFlags';
import { useClientUnread } from '../../messages/data/clientStore';
import './MobileTabBar.css';

// A salon's reply while the customer is elsewhere in the app (loaded only when signed in)
const ClientBanner = lazy(() => import('../../messages/push/NewMessageBanner').then((m) => ({ default: m.ClientBanner })));

// Explore/Search icon
const ExploreIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#1a1a1a" : "rgba(255,255,255,0.92)"} strokeWidth="2" width="24" height="24">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

// Calendar/Bookings icon
const BookingsIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#1a1a1a" : "rgba(255,255,255,0.92)"} strokeWidth="2" width="24" height="24">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Speech bubble — Messages with salons (Beu Messages)
const MessagesIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#1a1a1a" : "rgba(255,255,255,0.92)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Unread salon messages, on the Messages tab. Only rendered when signed in,
// so a signed-out visitor never starts the Messages store.
const MessagesBadge = () => {
  const unread = useClientUnread();
  if (!unread) return null;
  return <span className="mobile-tab-badge">{unread > 9 ? "9+" : unread}</span>;
};

// Gift/present icon — Wallet (gift cards + loyalty)
const WalletIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#1a1a1a" : "rgba(255,255,255,0.92)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
    <rect x="3" y="8" width="18" height="13" rx="1.5" />
    <path d="M3 12h18M12 8v13" />
    <path d="M12 8S10.5 3.5 8 4.2C6.2 4.7 6.5 7.5 9 8M12 8s1.5-4.5 4-3.8c1.8.5 1.5 3.3-1 3.8" />
  </svg>
);

// User/Account icon
const AccountIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={active ? "#1a1a1a" : "rgba(255,255,255,0.92)"} strokeWidth="2" width="24" height="24">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const TAB_CONFIG = [
  {
    id: 'explore',
    label: 'Explore',
    icon: ExploreIcon,
    path: '/',
    matchPaths: ['/', '/salon', '/search']
  },
  {
    id: 'bookings',
    label: 'Bookings',
    icon: BookingsIcon,
    path: '/dashboard/bookings',
    matchPaths: ['/dashboard/bookings', '/bookings']
  },
  {
    id: 'messages',
    label: 'Messages',
    icon: MessagesIcon,
    path: '/dashboard/messages',
    matchPaths: ['/dashboard/messages'],
    flag: MESSAGES_VISIBLE,
  },
  {
    id: 'wallet',
    label: 'Wallet',
    icon: WalletIcon,
    path: '/dashboard/wallet',
    matchPaths: ['/dashboard/wallet', '/dashboard/loyalty']
  },
  {
    id: 'account',
    label: 'Account',
    icon: AccountIcon,
    path: '/dashboard/account',
    matchPaths: ['/dashboard/account', '/dashboard/reviews', '/dashboard/help', '/dashboard/marketing-preferences', '/profile', '/settings']
  }
];

// Routes where the tab bar should render. Everything else returns null so
// hoisting the bar to the router level doesn't leak it onto auth, checkout,
// admin, or salon/professional pages that never had it before.
const VISIBLE_ROUTES = [
  { exact: '/' },
  { prefix: '/social-login' },
  { prefix: '/dashboard' },
];

const shouldShowTabBar = (pathname) =>
  VISIBLE_ROUTES.some((rule) =>
    rule.exact !== undefined
      ? pathname === rule.exact
      : pathname === rule.prefix || pathname.startsWith(rule.prefix + '/')
  );

const MobileTabBar = ({ isLoggedIn: isLoggedInProp }) => {
  const history = useHistory();
  const location = useLocation();
  const navRef = useRef(null);
  const itemRefs = useRef({});
  // The sliding tonal pill behind the active tab. Measured from the real
  // button geometry so it stays correct across label changes ("Account" vs
  // "Log In"), font loading, and viewport resizes.
  const [indicator, setIndicator] = useState(null);

  const visible = shouldShowTabBar(location.pathname);

  // Determine which tab is active based on current path
  const getActiveTab = () => {
    const currentPath = location.pathname;

    for (const tab of TAB_CONFIG) {
      if (tab.matchPaths.some(path =>
        currentPath === path || currentPath.startsWith(path + '/')
      )) {
        return tab.id;
      }
    }

    // Default to explore if no match
    return 'explore';
  };

  const activeTab = getActiveTab();

  useLayoutEffect(() => {
    if (!visible) return undefined;
    const measure = () => {
      const nav = navRef.current;
      const el = itemRefs.current[activeTab];
      if (!nav || !el) return;
      const navRect = nav.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      setIndicator({ x: rect.left - navRect.left, width: rect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [visible, activeTab]);

  if (!visible) {
    return null;
  }

  // Logged-in state comes from localStorage (matches PrivateRoute; redux may
  // not be hydrated yet on a cold start with a persisted token). Read ONCE
  // per render - every login/logout navigates, navigation re-renders the
  // bar, so this stays current without re-reading per tab.
  const isLoggedIn = isLoggedInProp !== undefined
    ? isLoggedInProp
    : !!localStorage.getItem('accessToken');

  const handleTabClick = (tab) => {
    if (activeTab !== tab.id) hapticTick();
    if (!isLoggedIn && tab.id !== 'explore') {
      // Redirect to login for protected tabs when not logged in
      history.push('/login');
      return;
    }
    history.push(tab.path);
  };

  return (
    <>
    {MESSAGES_VISIBLE && isLoggedIn && <Suspense fallback={null}><ClientBanner history={history} /></Suspense>}
    <nav ref={navRef} className="mobile-tab-bar" role="navigation" aria-label="Main navigation">
      {indicator && (
        <span
          className="mobile-tab-indicator"
          style={{ transform: `translateX(${indicator.x}px)`, width: `${indicator.width}px` }}
          aria-hidden
        />
      )}
      {TAB_CONFIG.filter((tab) => tab.flag !== false).map((tab) => {
        const isActive = activeTab === tab.id;
        const IconComponent = tab.icon;

        // Icon-only bar: the text lives in aria-label so screen readers still
        // announce "Account" / "Log In" per state.
        const displayLabel = tab.id === 'account' && !isLoggedIn ? 'Log In' : tab.label;

        return (
          <button
            key={tab.id}
            ref={(el) => { itemRefs.current[tab.id] = el; }}
            className={`mobile-tab-item ${isActive ? 'active' : ''}`}
            onClick={() => handleTabClick(tab)}
            aria-label={displayLabel}
            aria-current={isActive ? 'page' : undefined}
          >
            <IconComponent active={isActive} />
            {tab.id === 'messages' && isLoggedIn && <MessagesBadge />}
          </button>
        );
      })}
    </nav>
    </>
  );
};

export default MobileTabBar;
